<template>
	<div class="stream-toolbar">
		<nav v-if="showNavigationToolbar"
			class="stream-toolbar__navigation"
		>
			<SidebarButton
				v-if="featureOpenSidebar"
				disposition="OPEN"
				size="SMALL"
				type="TERTIARY"
				@click="$emit( `openClick` )"
			/>
			<CalendarButton
				v-if="featureChangeDate"
				size="SMALL"
				type="TERTIARY"
				@change="$emit( `dateChanged`, $event )"
			/>
			<PopMenu
				v-if="featureRecurrence"
				:options="menuItems"
				@select="onMenuSelection"
			>
				<template #button="{ onClick, }">
					<CalendarClockButton
						class="schedule-recurrence-button"
						v-if="featureRecurrence"
						size="SMALL"
						type="TERTIARY"
						@click="onClick"
					/>
				</template>
			</PopMenu>
			<TeamingSegmentedButton
				class="doctype-button"
				v-if="featureChangeDocumentType"
				left-segment-name="shared"
				right-segment-name="private"
				:active-segment-name="documentType"
				orientation="HORIZONTAL"
				@change="onDocumentTypeChanged"
			>
				<template v-slot:left-segment>
					<div class="doctype-segment">
						<FontAwesomeIcon
							class="doctype-segment-icon"
							:icon="[ `fas`, `user-group` ]"
						/>
						<span>{{ translate( "sharedNotesButtonText" )}}</span>
					</div>
				</template>
				<template v-slot:right-segment>
					<div class="doctype-segment">
						<FontAwesomeIcon
							class="doctype-segment-icon"
							:icon="[ `fas`, `eye-slash` ]"
						/>
						<span>{{ translate( "privateNotesButtonText" )}}</span>
					</div>
				</template>
			</TeamingSegmentedButton>
		</nav>
		<TeamingButton2
			v-if="showAddButton"
			size="SMALL"
			@click="onNewNote"
		>
			{{ translate( `newNoteButtonText` ) }}
		</TeamingButton2>
		<div v-if="showSpacer">
			<ToolbarSpacer class="stream-toolbar__spacer" />
		</div>
		<LabeledInputField
			v-if="showVirtualMeetingField"
			class="virtual-meeting-url"
			:blur-is-input="false"
			:is-valid="isVirtualMeetingUrlValid"
			:placeholder="translate( `virtualMeetingUrlPlaceholder` )"
			:validation-error="{ message: translate( `virtualMeetingUrlError` ) }"
			:value="virtualMeetingUrl"
			@blur="onVirtualMeetingUrlBlur"
			@focus="isVirtualMeetingUrlFocused = true"
			@input="onVirtualMeetingUrlInput"
			@paste="onVirtualMeetingUrlPaste"
			name="virtualMeetingUrl"
		>
			<template #left-icon>
				<FontAwesomeIcon :icon="[ `fas`, `message-bot`, ]" />
			</template>
		</LabeledInputField>
		<TeamingButton2
			v-if="showGoButton"
			class=""
			style="margin-top: 0.2rem;"
			size="SMALL"
			@click="onGoClicked"
		>
			{{ needsZoomAuthorization ? translate( `authorizeAndGoButtonText` ) : translate( `goButtonText` ) }}
		</TeamingButton2>
		<div class="stream-toolbar__spacer" v-if="showOrLabel">
			{{ translate( `orLabel` ) }}
		</div>
		<TeamingButton2
			v-if="showUploadButton"
			class="upload-button"
			size="SMALL"
			type="QUATERNARY"
			@click="$emit( `newUploadNote` )"
		>
			<template #left-icon>
				<FontAwesomeIcon :icon="[ `far`, `cloud-arrow-up` ]" />
			</template>
			<template #default>
				{{ translate( `uploadButtonText` ) }}
			</template>
		</TeamingButton2>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import {
	VirtualMeetingType
} from "@teaming/common-enums";

import {
	CalendarButton,
	CalendarClockButton,
	EventsMixin,
	LabeledInputField, makePopMenuOption, PopMenu, SidebarButton,
	TeamingButton2, TeamingSegmentedButton,
	ToolbarSpacer,
	TranslateMixin
} from "@teaming/common-ui";

import {
	resolveVirtualMeetingType,
	VirtualMeetingDomains
} from "../../domain";

import {
	takeNotesForMeEvent
} from "../../events";

export default {
	name: "StreamToolbar",
	teamingPackage: "common-team",
	mixins: [
		TranslateMixin,
		EventsMixin,
	],
	emits: [
		"dateChanged",
		"documentTypeChange",
		"newBotNote",
		"newNote",
		"newUploadNote",
		"openClick",
		"scheduleRecurrence",
	],
	components: {
		PopMenu,
		SidebarButton,
		CalendarButton,
		TeamingSegmentedButton,
		CalendarClockButton,
		LabeledInputField,
		TeamingButton2,
		ToolbarSpacer,
	},
	props: {
		documentName: {
			type: String,
			required: true,
		},
		documentType: {
			type: String,
			required: true,
		},
		featureAddNewNote: {
			type: Boolean,
			default: true,
		},
		featureChangeDate: {
			type: Boolean,
			default: false,
		},
		featureChangeDocumentType: {
			type: Boolean,
			default: true,
		},
		featureCloseSidebar: {
			type: Boolean,
			default: true,
		},
		featureJoinVirtualMeeting: {
			type: Boolean,
			default: true,
		},
		featureOpenSidebar: {
			type: Boolean,
			default: false,
		},
		featureRecurrence: {
			type: Boolean,
			default: true,
		},
		featureUploadRecording: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			initialized: false,
			activeSubDocId: "",
			isVirtualMeetingUrlValid: true,
			isVirtualMeetingUrlFocused: false,
			virtualMeetingUrl: "",
			focusHandler: null,
			needsZoomAuthorization: false,
		};
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"isZoomAuthorized",
		] ),
		menuItems() {
			const { translate, } = this;
			return [
				{
					text: translate( "recurringMeetingOptionText" ),
					value: "createRecurringMeeting",
				},
				{
					text: translate( "oneTimeMeetingOptionText" ),
					value: "createOneTimeMeeting",
				},
				{
					isSeparator: true,
				},
				{
					text: translate( "manageScheduleOptionText" ),
					value: "scheduleRecurrence",
				},
			].map( makePopMenuOption );
		},
		showNavigationToolbar() {
			const {
				featureChangeDate,
				featureChangeDocumentType,
				featureCloseSidebar,
				featureOpenSidebar,
			} = this;
			return [
				featureChangeDate,
				featureChangeDocumentType,
				featureCloseSidebar,
				featureOpenSidebar,
			].find( f => !!f );
		},
		showAddButton() {
			const {
				featureAddNewNote,
				isVirtualMeetingUrlFocused,
			} = this;
			return featureAddNewNote && !isVirtualMeetingUrlFocused;
		},
		showSpacer() {
			const {
				featureChangeDocumentType,
				featureAddNewNote,
				featureJoinVirtualMeeting,
				featureUploadRecording,
				isVirtualMeetingUrlFocused,
			} = this;
			return ![
				( featureChangeDocumentType || featureAddNewNote ),
				( featureJoinVirtualMeeting || featureUploadRecording ),
				!isVirtualMeetingUrlFocused,
			].includes( false );
		},
		showVirtualMeetingField() {
			return this.featureJoinVirtualMeeting;
		},
		showUploadButton() {
			const {
				featureUploadRecording,
				isVirtualMeetingUrlFocused,
			} = this;
			return featureUploadRecording && !isVirtualMeetingUrlFocused;
		},
		showGoButton() {
			const {
				featureJoinVirtualMeeting,
				isVirtualMeetingUrlFocused,
			} = this;
			return featureJoinVirtualMeeting && isVirtualMeetingUrlFocused;
		},
		showOrLabel() {
			const {
				featureJoinVirtualMeeting,
				showUploadButton,
			} = this;
			return featureJoinVirtualMeeting && showUploadButton;
		},
	},
	methods: {
		onMenuSelection( { value, } ) {
			// note this spot causes an emits error since the value is mutable/not defined in the emits array
			this.$emit( value );
		},
		resetVirtualMeetingUrlState() {
			this.isVirtualMeetingUrlValid = true;
			this.virtualMeetingUrl = "";
			this.isVirtualMeetingUrlFocused = false;
		},
		async onVirtualMeetingUrlBlur() {
			// buys time for the go buttons click event to register
			// before we hide the control
			setTimeout( () => {
				const { virtualMeetingUrl, } = this;
				if ( virtualMeetingUrl === "" ) {
					this.resetVirtualMeetingUrlState();
				} else {
					this.isVirtualMeetingUrlValid = !!resolveVirtualMeetingType( virtualMeetingUrl );
				}
				// eslint-disable-next-line no-magic-numbers
			}, 200 );
		},
		onVirtualMeetingUrlInput( e ) {
			const value = e.target.value.trim();
			this.virtualMeetingUrl = value;
			this.checkForZoomLinkAndAuthorization( value );
		},
		onVirtualMeetingUrlPaste( e ) {
			// NOTE: for some reason Google decided to be cute and generate
			// Google Meet URLs without the protocol (e.g., meet.google.com/nkb-uyeu-yxz),
			// so we're automagically prepending it here to help the user out
			const { value, } = e.target;
			const googleMeetDomain = VirtualMeetingDomains[ VirtualMeetingType.GOOGLE_MEET ];
			if ( value.startsWith( googleMeetDomain ) ) {
				this.virtualMeetingUrl = `https://${ value }`;
			}

			this.checkForZoomLinkAndAuthorization( value );
		},
		checkForZoomLinkAndAuthorization( value ) {
			const virtualMeetingType = resolveVirtualMeetingType( value );

			this.needsZoomAuthorization = virtualMeetingType === VirtualMeetingType.ZOOM && !this.isZoomAuthorized;
		},
		async onGoClicked() {
			const { virtualMeetingUrl, } = this;
			this.isVirtualMeetingUrlValid = !!resolveVirtualMeetingType( virtualMeetingUrl );
			if ( !this.isVirtualMeetingUrlValid ) {
				return;
			}
			this.$emit( "newBotNote", {
				virtualMeetingUrl,
			} );

			await this.$nextTick();
			this.resetVirtualMeetingUrlState();
		},
		onNewNote() {
			this.$emit( "newNote" );
		},
		onDocumentTypeChanged( docType ) {
			this.$emit( "documentTypeChange", { docType, } );
		},
		editorActivated( subDocId ) {
			this.activeSubDocId = subDocId;
		},
		editorDeactivated() {
			this.activeSubDocId = "";
		},
		createListeners() {
			const { initialized, documentName, } = this;
			if ( initialized || !documentName ) {
				return;
			}
			this.focusHandler = () => document.querySelector( ".stream-toolbar input[name=virtualMeetingUrl]" ).focus();
			window.addEventListener( takeNotesForMeEvent().type, this.focusHandler );
			const registry = this.$documentRegistry();
			registry.on( `${ documentName }.editorActivated`, this.editorActivated );
			registry.on( `${ documentName }.editorDeactivated`, this.editorDeactivated );
			this.initialized = true;
		},
		disposeListeners() {
			const { documentName, } = this;
			const registry = this.$documentRegistry();
			registry.off( `${ documentName }.editorActivated`, this.editorActivated );
			registry.off( `${ documentName }.editorDeactivated`, this.editorDeactivated );
			window.removeEventListener( takeNotesForMeEvent().type, this.focusHandler );
		},
	},
	mounted() {
		this.createListeners();
	},
	updated() {
		this.createListeners();
	},
	beforeUnmount() {
		this.disposeListeners();
	},
};
</script>

<style lang="scss">
.stream-toolbar .stream-toolbar-virtual-meeting-url .labeled-input-field__input {
	margin-top: 0;
}

.stream-toolbar .doctype-button {
	.segment {
		@include font-and-line-for(0);
		padding: .35rem .5rem;
	}
}
</style>

<style lang="scss" scoped>
.stream-toolbar {
	display: flex;
	flex-direction: row;
	justify-content: stretch;
	align-items: center;
	gap: 0.5rem;

	@include for-size('sm') {
		gap: 1rem;
	}
}

.stream-toolbar .stream-toolbar__navigation {
	flex: 1;

	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;

	@include for-size('xl') {
		min-width: 23.5rem;
	}
}

.stream-toolbar .doctype-button {
	flex: 1;
}

.stream-toolbar .doctype-segment {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
}

.stream-toolbar .doctype-segment-icon {
	display: none;
	padding: 0.2rem 0.1rem;

	@include for-size('sm') {
		display: inline-flex;
	}
}

.stream-toolbar .schedule-recurrence-button {
	@include for-size('sm') {
		display: none;
	}
}

.stream-toolbar .virtual-meeting-url {
	flex: 3;
}

.stream-toolbar .upload-button {
	margin-top: 0.2rem;
}

.stream-toolbar .upload-button-text {
	display: none;

	@include for-size('md') {
		display: inline;
	}
}
</style>

